import { z } from "zod";
import { IsoDateSchema, optionalQuery } from "./primitives.js";
/**
 * Reports and the CA export — §11.2 (the secondary screen) and §17.4.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS SCREEN IS FOR, IN ONE SENTENCE: so that nobody re-types a month of
 * invoices into the CA's software by hand. §11.2 allows exactly one secondary
 * screen beyond the home tiles — "period selector … total billed, tax collected
 * and invoice count, plus a customer-wise summary and an export button wired to
 * §17.4. This screen exists mainly to feed the CA."
 *
 * FOUR DOCUMENT TYPES AND A LEDGER NOW EXIST, AND EACH BELONGS IN A DIFFERENT
 * COLUMN. Getting this wrong produces a report that looks right and files wrong,
 * so the rules are stated here, once, where both the API and the SPA read them:
 *
 *   INVOICES are the revenue and the output tax — ISSUED (and PENDING_IRN, which
 *     is unreachable in v1) only.
 *   CANCELLED INVOICES contribute ZERO and are NEVER silently omitted (§17.4):
 *     they appear in the register with their status, because the CA needs the
 *     gap in the number series explained. DRAFT invoices are in exactly the same
 *     position — §7.1 gives a converted invoice with no S.O. number a DRAFT
 *     status and it TAKES ITS NUMBER anyway, so a draft is a consumed serial
 *     that contributes nothing.
 *   CREDIT NOTES (§17.3) REDUCE output tax and are declared in GSTR-1's CDNR
 *     section. Every "total billed" and "tax charged" figure NETS THEM OFF, or
 *     the owner over-reports his liability — see {@link ReportSummaryResponse}.
 *   DELIVERY CHALLANS create NO liability and reach NO rupee figure anywhere in
 *     this module. Only their SERIAL RANGE is declared (§17.7, Rule 55: the
 *     value on a challan is the declared value of goods in transit, owed by
 *     nobody). {@link ReportSerialRange} is the ONE place a challan appears.
 *   DISCOUNTS (§15(3)) have ALREADY reduced the stored taxable value —
 *     `subtotal_paise` is net of both discount columns. Every figure here sums
 *     the NET column; the gross is not stored and must not be reconstructed.
 *   PAYMENTS are cash, not revenue. "Received in August" and "billed in August"
 *     are different questions and never share a caption — see
 *     {@link ReportSummaryResponse.received}.
 * ═════════════════════════════════════════════════════════════════════════════
 */
/* -------------------------------------------------------------------------- */
/* 1. The period                                                               */
/* -------------------------------------------------------------------------- */
/**
 * The three presets §11.2 names, spelled EXACTLY as the SPA's
 * `lib/date-presets.ts` already spells them.
 *
 * Same strings on both sides on purpose: the screen's period chip can be handed
 * to the API unchanged, so there is no mapping table to fall out of step. A
 * fourth preset is a spec change, not a convenience — §11.2 names three.
 */
export const REPORT_PERIOD_PRESETS = ["thisMonth", "lastMonth", "thisFy"];
/* -------------------------------------------------------------------------- */
/* 5. GET /reports/outstanding-ageing                                          */
/* -------------------------------------------------------------------------- */
/**
 * The four buckets, in order, and the boundaries are INCLUSIVE ON BOTH SIDES.
 *
 *   `0-30`  → `0 <= ageDays <= 30`
 *   `31-60` → `31 <= ageDays <= 60`
 *   `61-90` → `61 <= ageDays <= 90`
 *   `90+`   → `ageDays >= 91`
 *
 * So a bill 30 days old is in the first bucket and one 31 days old is in the
 * second; 60 → second, 61 → third, 90 → third, 91 → fourth. Stated here because
 * "which side does 30 fall on" is the sort of question that gets answered
 * differently in the API and in a chart legend.
 *
 * THE LAST BUCKET IS LABELLED `90+`, NOT "OVERDUE". This system models no credit
 * terms and stores no due date (§17.5), so calling a 91-day-old bill overdue
 * would be the app inventing an agreement the business never made.
 */
export const OUTSTANDING_AGE_BUCKETS = ["0-30", "31-60", "61-90", "90+"];
/* -------------------------------------------------------------------------- */
/* 6. Serial ranges (§17.4, GSTR-1 table 13)                                   */
/* -------------------------------------------------------------------------- */
/** The four numbered series this business issues (§5, §6). */
export const REPORT_SERIES_KINDS = ["invoice", "quotation", "challan", "creditNote"];
/* -------------------------------------------------------------------------- */
/* 7. The export (§17.4)                                                       */
/* -------------------------------------------------------------------------- */
/**
 * The GSTR-1 tables this export writes, ONE CSV EACH.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE FILE PER SECTION RATHER THAN ONE FILE WITH FOUR STACKED TABLES, and the
 * reason is CSV, not taste: the four tables have different columns, so stacking
 * them produces a file that is not rectangular — every parser and every
 * spreadsheet reads that as one malformed table, and "a number Excel truncates
 * is a support call". It is also how GSTR-1 is actually filed: the offline tool
 * takes the tables separately.
 *
 * The alternative — one download containing four files — needs a zip, which
 * needs a dependency, and CLAUDE.md says ask first.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const GSTR1_SECTIONS = ["b2b", "cdnr", "hsn", "docs"];
/* -------------------------------------------------------------------------- */
/* 8. Query schemas                                                            */
/* -------------------------------------------------------------------------- */
/**
 * `2026-27` (the `company.current_fy` form) or `26-27` (the form printed on
 * every invoice). Both spellings reach the API because both are in front of the
 * owner; the parser lives on the server (`parseFyStartYear`), and this schema
 * only proves the shape so a 400 names the field.
 */
export const REPORT_FY_REGEX = /^(\d{4}|\d{2})-(\d{2})$/;
/**
 * How every reports endpoint takes its period.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TWO SPELLINGS, ONE RESOLVER, AND THEY MAY NOT BE MIXED.
 *
 *   `?preset=thisMonth|lastMonth|thisFy` — what the screen's chip sends (§11.2).
 *   `?from=&to=`                        — an explicit inclusive day range.
 *
 * Both are resolved by the SAME server-side function, so the screen and the CSV
 * behind it describe the identical window. The mixture is REFUSED rather than
 * given a precedence rule: "from wins over preset" is a rule nobody reads, and
 * the failure it produces is a CSV for a month the owner was not looking at.
 *
 * THE EXPORT NEEDS THE EXPLICIT FORM AND THAT IS WHY IT EXISTS. The screen shows
 * a financial year; the CA files a month. On 3 September the owner must be able
 * to export August without first making the screen show August.
 *
 * `?fy=` names WHICH financial year, and is only meaningful with
 * `preset=thisFy`; absent means the one we are standing in, in India (§13).
 * ═══════════════════════════════════════════════════════════════════════════
 */
const ReportPeriodQueryShape = {
    preset: optionalQuery(z.enum(REPORT_PERIOD_PRESETS).optional()),
    fy: optionalQuery(z
        .string()
        .trim()
        .regex(REPORT_FY_REGEX, 'Financial year must look like "2026-27" or "26-27"')
        .optional()),
    from: optionalQuery(IsoDateSchema.optional()),
    to: optionalQuery(IsoDateSchema.optional()),
};
/**
 * The period rules, WRITTEN ONCE.
 *
 * Three schemas below share them, and a rule stated three times is a rule that
 * gets fixed once — the export would accept a mixture the screen refuses, which
 * is precisely the "CSV for a month the owner was not looking at" this guards.
 */
function refinePeriod(value, ctx) {
    const hasRange = value.from !== undefined || value.to !== undefined;
    if (hasRange && value.preset !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["preset"],
            message: "Give a period preset or a from/to range, not both — they would name different months.",
        });
    }
    if (hasRange && (value.from === undefined || value.to === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [value.from === undefined ? "from" : "to"],
            message: "A date range needs both from and to.",
        });
    }
    // Lexicographic on the `YYYY-MM-DD` head, as everywhere in this package: no
    // Date parsing, no zone, and `IsoDateSchema` has already proved the shape.
    if (value.from !== undefined && value.to !== undefined && value.from > value.to) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["to"],
            message: "The end of the range is before its start.",
        });
    }
}
export const ReportPeriodQuerySchema = z
    .object(ReportPeriodQueryShape)
    .strict()
    .superRefine(refinePeriod);
/**
 * The CSV endpoints' query: a period, plus how the file itself is written.
 *
 * `bom` — a UTF-8 byte-order mark, ON BY DEFAULT. Customer names in this
 * business are routinely Devanagari (the Marathi UI exists for a reason), and
 * Excel opened on a `.csv` with no BOM renders them as mojibake — a guaranteed
 * support call the first time the CA opens the file. A BOM is legal UTF-8 and
 * every mainstream parser skips it; the flag exists because some older importer
 * somewhere chokes on one, and the owner cannot be told "wait for a deploy" two
 * days before the 11th.
 */
export const ReportExportQuerySchema = z
    .object({
    ...ReportPeriodQueryShape,
    bom: optionalQuery(z.enum(["true", "false"]).optional()),
})
    .strict()
    .superRefine(refinePeriod);
/** `GET /reports/gstr1.csv` — the period, the BOM flag, and WHICH table. */
export const Gstr1ExportQuerySchema = z
    .object({
    ...ReportPeriodQueryShape,
    bom: optionalQuery(z.enum(["true", "false"]).optional()),
    /**
     * REQUIRED, with no default. A default would silently hand back one table of
     * four to a caller who asked for "the GSTR-1 export" and believed he had it.
     */
    section: z.enum(GSTR1_SECTIONS, {
        errorMap: () => ({ message: "Section must be one of b2b, cdnr, hsn, docs" }),
    }),
})
    .strict()
    .superRefine(refinePeriod);
//# sourceMappingURL=reports.js.map